import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import "./PlanRow.css";

export default function PlanRow({ pkg, onGetAccess }) {
  const { t } = useLanguage();
  const id = useId();
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [focused, setFocused] = useState(false);
  const open = pinned || (!dismissed && (hovered || focused));
  const libraries = pkg.libraries || [];
  const periods = { 30: "/ Month", 60: "/ 2 Months", 90: "/ 3 Months", 180: "/ 6 Months", 365: "/ Year" };
  const period = periods[pkg.duration_days] ? t(periods[pkg.duration_days]) : t("/ {days} Days", { days: pkg.duration_days });
  const features = pkg.description_points?.length ? pkg.description_points : [
    "All Video Categories", "Stream on Any Device", t("{days} days", { days: pkg.duration_days }),
  ];

  const toggle = () => {
    // First click pins a hover preview; a second click collapses it.
    if (pinned) { setPinned(false); setDismissed(true); }
    else { setPinned(true); setDismissed(false); }
  };

  return <article className={`hpa-plan-row ${open ? "is-open" : ""} ${pkg.is_ultimate_program ? "is-ultimate" : ""}`}
    onPointerEnter={(event) => {
      if (event.pointerType === "mouse" && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
        setHovered(true); setDismissed(false);
      }
    }}
    onPointerLeave={() => { setHovered(false); }}
    onKeyDown={(event) => {
      if (event.key === "Escape") {
        setPinned(false); setDismissed(true);
        document.getElementById(`${id}-toggle`)?.focus();
      }
    }}>
    <div className="hpa-plan-row__header">
      <button type="button" id={`${id}-toggle`} className="hpa-plan-row__summary" aria-expanded={open} aria-controls={`${id}-details`} onClick={toggle}>
        <span className="hpa-plan-row__identity">
          {pkg.promotion_badge && <span className="hpa-plan-row__badge">{pkg.promotion_badge}</span>}
          <span className="hpa-plan-row__name">{pkg.name}</span>
          <span className="hpa-plan-row__hint">{t(pinned ? "Hide details" : "View details")} <span aria-hidden="true">↗</span></span>
        </span>
        <span className="hpa-plan-row__price">
          {Number(pkg.original_price) > Number(pkg.price) && <del>{pkg.currency} {pkg.original_price}</del>}
          <strong><small>{pkg.currency}</small> {pkg.price}</strong>
          <span>{period}</span>
        </span>
        <span className="hpa-plan-row__toggle" aria-hidden="true"><span>+</span></span>
      </button>
      <button type="button" className="hpa-plan-row__cta" onClick={onGetAccess}>
        {t(pkg.is_ultimate_program ? "Request Your Program" : "Get Access")} <span aria-hidden="true">↗</span>
      </button>
    </div>
    <div id={`${id}-details`} className="hpa-plan-row__reveal" aria-hidden={!open}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <div className="hpa-plan-row__clip">
        <div className="hpa-plan-row__details">
          <div className="hpa-plan-row__access">
            <h3>{t(pkg.is_ultimate_program ? "Full Academy Access" : "Gives Access To")}</h3>
            {pkg.promotion_message && <p className="hpa-plan-row__promotion">{pkg.promotion_message}</p>}
            {pkg.is_ultimate_program ? <p>{t("Every current and future library")}</p> : <>
              {pkg.is_dynamic && <p>{t("Choose {count} libraries", { count: pkg.library_selection_count })}</p>}
              <div className="hpa-plan-row__libraries">{libraries.map((lib) =>
                <Link key={lib.id} to={`/library/${lib.slug}`} tabIndex={open ? 0 : -1}>{lib.name}</Link>)}</div>
            </>}
          </div>
          <div className="hpa-plan-row__features">
            <h3>{t("WHAT YOU GET")}</h3>
            <ul>{features.map((point, index) => <li key={index}><span aria-hidden="true">✓</span>{t(point)}</li>)}</ul>
          </div>
        </div>
      </div>
    </div>
  </article>;
}
